import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import type { FC, PropsWithChildren, ReactElement } from 'react';
import { useEffect, useState, useRef } from 'react';

import IdentifyOnUserChange from './IdentifyOnUserChange';

import { useAppSelector } from 'features/app/hooks';
import { agentNumberSelector } from 'features/user/store';
import { ModeStorageService } from 'features/multi-mode/services';
import { envConfig } from 'features/environment/services';
import { LoggerService } from 'features/logger/services';

export const LaunchDarklyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [provider, setProvider] = useState<ReactElement | null>(null);
  const selectedAgentNumber = useAppSelector(agentNumberSelector);
  const initialAgentNumberRef = useRef<string | undefined>(
    selectedAgentNumber ?? undefined
  );

  useEffect(() => {
    const initProvider = async () => {
      try {
        const LDProvider = await asyncWithLDProvider({
          clientSideID: envConfig.launchDarklyClientSideId,
          context: {
            kind: 'user',
            anonymous: true,
            wantsAllModes: localStorage.getItem(
              'launchDarklyContext/wantsAllModes'
            ),
            agentNumber: initialAgentNumberRef.current || undefined,
            mode: ModeStorageService.getMode() || undefined,
          },
        });
        if (LDProvider) {
          setProvider(
            <LDProvider>
              <IdentifyOnUserChange />
              {children}
            </LDProvider>
          );
        } else {
          // Fallback in case LDProvider fails to load
          setProvider(<>{children}</>);
        }
      } catch (e) {
        LoggerService.error(e);
        setProvider(<>{children}</>);
      }
    };

    initProvider();
  }, [children]);

  return provider;
};
